import React from "react";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  AdminPanelSettings,
  CalendarToday,
  ChevronRight,
  Groups,
  Warning,
} from "@mui/icons-material";
import dayjs from "dayjs";
import type { ClubMembership } from "./types";
import { getNextTrainingDate, getUnpaidInvoiceCount } from "./clubSummary";

const fontFamily =
  "'Fredoka', 'Nunito', 'Comfortaa', 'Quicksand', sans-serif";

interface ClubPickerProps {
  memberships: ClubMembership[];
  onSelect: (clubId: number) => void;
}

const formatDate = (dateString: string) => dayjs(dateString).format("DD/MM/YYYY");

const ClubPicker: React.FC<ClubPickerProps> = ({ memberships, onSelect }) => (
  <Stack spacing={2}>
    {memberships.map((club) => {
      const unpaidCount = getUnpaidInvoiceCount(club);
      const nextTraining = getNextTrainingDate(club);

      return (
        <Paper
          key={club.id}
          component="button"
          type="button"
          onClick={() => onSelect(club.id)}
          sx={{
            width: "100%",
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: "2px solid",
            borderColor: unpaidCount > 0 ? "#FFCDD2" : "#E8F5E9",
            bgcolor: "white",
            textAlign: "left",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 2,
            transition: "transform 0.2s, box-shadow 0.2s",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "50%",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Groups sx={{ fontSize: 28, color: "white" }} />
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#2D3748",
                fontFamily,
                mb: 0.75,
              }}
            >
              {club.name}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {club.is_admin && (
                <Chip
                  icon={<AdminPanelSettings />}
                  label="Club Admin"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontFamily,
                    background: "linear-gradient(45deg, #FFD93D 30%, #FFC107 90%)",
                    color: "#000",
                  }}
                />
              )}
              {unpaidCount > 0 && (
                <Chip
                  icon={<Warning />}
                  label={`${unpaidCount} payment${unpaidCount === 1 ? "" : "s"} due`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontFamily,
                    background: "linear-gradient(45deg, #FF6B6B 30%, #EE5A6F 90%)",
                    color: "white",
                  }}
                />
              )}
              {nextTraining && (
                <Chip
                  icon={<CalendarToday sx={{ fontSize: "16px !important" }} />}
                  label={`Next: ${nextTraining.group_name} · ${formatDate(nextTraining.training_date)}`}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontFamily,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              )}
            </Box>
          </Box>

          <ChevronRight sx={{ color: "text.secondary", flexShrink: 0 }} />
        </Paper>
      );
    })}
  </Stack>
);

export default ClubPicker;
